import { useQuery } from "react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import Layout from "./components/Layout/Layout";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Login from "./pages/Auth/Login";
import ChangePassword from "./pages/Auth/ChangePassword";
import Dashboard from "./pages/Dashboard/Dashboard";
import CreateDriver from "./pages/Drivers/CreateDriver";
import DriverDetails from "./pages/Drivers/DriverDetails";
import DriverLocation from "./pages/Drivers/DriverLocation";
import Drivers from "./pages/Drivers/Drivers";
import DriverWorkHistory from "./pages/Drivers/DriverWorkHistory";
import PendingDrivers from "./pages/Drivers/PendingDrivers";
// import DriverAttendance from './pages/Drivers/driverAttendance';
import Hotspot from "./pages/Hotspot/hotspot";
import PassengerDetails from "./pages/Passengers/PassengerDetails";
import Passengers from "./pages/Passengers/Passengers";
import PeakHours from "./pages/PeakHours/PeakHours";
import Roles from "./pages/Roles/Roles";
import ActiveTrips from "./pages/Trips/ActiveTrips";
import ActingTrip from "./pages/Trips/ActingTrip";
import CreateTrip from "./pages/Trips/CreateTrip";
import ScheduleTrips from "./pages/Trips/ScheduleTrips";
import TripDetails from "./pages/Trips/TripDetails";
import Trips from "./pages/Trips/Trips";

import Fare from "./pages/Fare/Fare";
import ActingDriverFare from "./pages/ActingDriverFare/ActingDriverFare";
import ReferralProgram from "./pages/Referral/ReferralProgram";
import DriverSim from "./pages/DriverSim/DriverSim";
import SubscriptionPlan from "./pages/SubscriptionPlan/SubscriptionPlan";
import DueDetails from "./pages/Payments/DueDetails";
import Payments from "./pages/Payments/Payments";
import PaymentTrips from "./pages/Payments/PaymentTrips";

// Financial Dashboard imports
import AppConfig from "./pages/AppConfig/AppConfig";
import AppConfigDetails from "./pages/AppConfig/AppConfigDetails";
import CreateAppConfig from "./pages/AppConfig/CreateAppConfig";
import DriverStatistics from "./pages/Drivers/driverStatistics";
import DriverStats from "./pages/Drivers/DriverStats";
import DriverWorkHistoryDetails from "./pages/Drivers/DriverWorkHistoryDetails";
import RideMatch from "./pages/Drivers/RideMatch";
import Feedback from "./pages/Feedback/feedback";
import DuePaymentsOverview from "./pages/FinancialDashboard/DuePaymentsOverview";
import EarningsReport from "./pages/FinancialDashboard/EarningsReport";
import RegionalOfficeDueDetails from "./pages/FinancialDashboard/RegionalOfficeDueDetails";
import GST from "./pages/GST/gst";
import CreateRegionalOffice from "./pages/RegionalOffices/CreateRegionalOffice";
import EditRegionalOffice from "./pages/RegionalOffices/EditRegionalOffice";
import RegionalOfficeDetails from "./pages/RegionalOffices/RegionalOfficeDetails";
import RegionalOffices from "./pages/RegionalOffices/RegionalOffices";
import Settings from "./pages/Settings/Settings";
import RideMatchSettings from "./pages/RideMatchSettings/RideMatchSettings";
import DiscordSettings from "./pages/Settings/DiscordSettings";
import SidebarPreview from "./pages/UiPreview/SidebarPreview";
import Tickets from "./pages/Tickets/Tickets";
import CreateVehicle from "./pages/Vehicles/CreateVehicle";
import EditVehicle from "./pages/Vehicles/EditVehicle";
import VehicleDetails from "./pages/Vehicles/VehicleDetails";
import Vehicles from "./pages/Vehicles/Vehicles";
import VehicleSwitchRequests from "./pages/Vehicles/VehicleSwitchRequests";
import CreateVendor from "./pages/Vendors/CreateVendor";
import VendorDetails from "./pages/Vendors/VendorDetails";
import Vendors from "./pages/Vendors/Vendors";
import { api } from "./utils/api";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Health Check Component
const HealthCheck = () => {
  const { isLoading, error } = useQuery("health", () => api.get("/health"));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-danger-600 mb-4">
            Server Connection Error
          </h1>
          <p className="text-gray-600 mb-4">
            Unable to connect to the server. Please check your connection and
            try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Main App Component
const AppContent = () => {
  return (
    <div className="App">
      <HealthCheck />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Temporary: shadcn sidebar-07 preview. Outside <Layout> on purpose —
            it is a full app shell and would otherwise nest two sidebars.
            Remove this route and pages/UiPreview once evaluated. */}
        <Route path="/ui-preview" element={<SidebarPreview />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Driver Routes */}
          <Route path="drivers" element={<Drivers />} />
          <Route path="drivers/pending" element={<PendingDrivers />} />
          {/* <Route path="drivers/attendance" element={<DriverAttendance />} /> */}
          <Route path="drivers/create" element={<CreateDriver />} />
          <Route path="drivers/:id" element={<DriverDetails />} />
          <Route path="drivers/:id/location" element={<DriverLocation />} />
          <Route path="drivers/:id/statistics" element={<DriverStatistics />} />
          {/* <Route path="drivers/:id/work-history" element={<DriverWorkHistory />} /> */}
          <Route path="drivers/stats" element={<DriverStats />} />
          <Route path="drivers/ridematchs" element={<RideMatch />} />
          <Route
            path="drivers/driverworkhistory"
            element={<DriverWorkHistory />}
          />
          <Route
            path="drivers/driverworkhistory/:id"
            element={<DriverWorkHistoryDetails />}
          />
          {/* Passenger Routes */}
          <Route path="passengers" element={<Passengers />} />
          <Route path="passengers/:id" element={<PassengerDetails />} />

          {/* Trip Routes */}
          <Route path="trips" element={<Trips />} />
          <Route path="trips/create" element={<CreateTrip />} />
          <Route path="trips/active" element={<ActiveTrips />} />
          <Route path="trips/acting" element={<ActingTrip />} />
          <Route path="trips/schedule" element={<ScheduleTrips />} />
          <Route path="trips/:id" element={<TripDetails />} />
          <Route path="hotspot" element={<Hotspot />} />
          <Route path="peak-hours" element={<PeakHours />} />

          {/* Regional Office Routes */}
          <Route path="regional-offices" element={<RegionalOffices />} />
          <Route
            path="regional-offices/create"
            element={<CreateRegionalOffice />}
          />
          <Route
            path="regional-offices/:id"
            element={<RegionalOfficeDetails />}
          />
          <Route
            path="regional-offices/:id/edit"
            element={<EditRegionalOffice />}
          />

          {/* Vendor Routes */}
          <Route path="vendors" element={<Vendors />} />
          <Route path="vendors/create" element={<CreateVendor />} />
          <Route path="vendors/:id" element={<VendorDetails />} />

          {/* Vehicle Routes */}
          <Route path="vehicles" element={<Vehicles />} />
          <Route path="vehicles/switch-requests" element={<VehicleSwitchRequests />} />
          <Route path="vehicles/create" element={<CreateVehicle />} />
          <Route path="vehicles/:id" element={<VehicleDetails />} />
          <Route path="vehicles/:id/edit" element={<EditVehicle />} />

          {/* Ticket Routes */}
          <Route path="tickets" element={<Tickets />} />

          {/* Role Management Routes */}
          <Route path="roles" element={<Roles />} />

          {/* Fare Configuration Routes */}
          <Route path="fare" element={<Fare />} />
          <Route path="acting-driver-fare" element={<ActingDriverFare />} />
          <Route path="referral" element={<ReferralProgram />} />
          <Route path="driver-sim" element={<DriverSim />} />
          <Route path="subscription-plan" element={<SubscriptionPlan />} />

          {/* Payment Routes */}
          <Route path="payments/*" element={<Payments />} />
          <Route path="payments/due/:officeId" element={<DueDetails />} />
          <Route
            path="payments/due/driver/:driverId/trips"
            element={<PaymentTrips />}
          />
          <Route
            path="payments/due/vendor/:vendorId/trips"
            element={<PaymentTrips />}
          />

          {/* Financial Dashboard Routes */}
          <Route
            path="financial"
            element={<Navigate to="/financial/due" replace />}
          />
          <Route
            path="financial/due/:officeId"
            element={<RegionalOfficeDueDetails />}
          />
          <Route path="financial/due" element={<DuePaymentsOverview />} />
          <Route path="financial/earnings" element={<EarningsReport />} />
          <Route path="financial/gst" element={<GST />} />

          <Route path="config" element={<AppConfig />} />
          <Route path="config/create" element={<CreateAppConfig />} />
          <Route path="config/:id" element={<AppConfigDetails />} />
          <Route path="feedback" element={<Feedback />} />

          {/* Settings Routes */}
          <Route path="settings" element={<Settings />} />
          <Route path="ride-match-settings" element={<RideMatchSettings />} />
          <Route path="discord-settings" element={<DiscordSettings />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

// Root App Component with Providers
const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
